import React, { useEffect, useState } from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'
import TagSelector from '../../../components/TagSelector'

const LANGUAGES = [
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'en', name: 'English' },
]

export default function New({ tags = [] }) {
  const { auth } = usePage().props
  const userLocale = auth?.user?.native_language || 'en'
  
  const [activeTab, setActiveTab] = useState('manual')
  const [isGenerating, setIsGenerating] = useState(false)
  const [statusUrl, setStatusUrl] = useState(null)
  const [queuedPrompt, setQueuedPrompt] = useState(null)
  const [aiPrompt, setAiPrompt] = useState({
    theme: '',
    target_lang: 'fr',
    native_lang: 'English',
    length: 'short'
  })

  const { data, setData, post, processing, errors } = useForm({
    title: '',
    tag_ids: [],
    difficulty: 1,
    language: 'fr',
    content_raw: '',
    image_url: '',
    image_file: null,
    tokens: null, // If populated, backend will use these instead of re-tokenizing
    spans: null // Phrase-level gloss layer (optional; AI flow may provide)
  })

  function submit(e) {
    e.preventDefault()
    post('/admin/texts')
  }

  async function handleGenerate() {
    setIsGenerating(true)
    setStatusUrl(null)
    setQueuedPrompt(null)
    let queued = false
    try {
      const response = await fetch('/admin/texts/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(aiPrompt)
      })

      const contentType = response.headers.get('content-type') || ''
      const isJson = contentType.includes('application/json')

      if (!response.ok) {
        // Gateways / proxies often return an HTML error page for 502/504.
        const bodyText = await response.text()
        throw new Error(`Generation failed (${response.status}). ${isJson ? '' : bodyText.slice(0, 200)}`)
      }

      if (!isJson) {
        const bodyText = await response.text()
        throw new Error(`Generation failed: expected JSON, got "${contentType}". ${bodyText.slice(0, 200)}`)
      }

      const result = await response.json()

      if (result.queued && result.status_url) {
        queued = true
        setStatusUrl(result.status_url)
        setQueuedPrompt({ ...aiPrompt })
        return
      }
      
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'text_generated', {
          'theme': aiPrompt.theme,
          'target_lang': aiPrompt.target_lang,
          'length': aiPrompt.length
        })
      }

      // Auto-fill form (note: tags need to be selected manually)
      setData(prev => ({
        ...prev,
        title: result.title,
        content_raw: result.content_raw,
        language: result.language || aiPrompt.target_lang,
        image_url: result.image_url,
        tokens: result.tokens,
        spans: result.spans,
        questions: result.questions
      }))
      
      // Switch to manual tab to review
      setActiveTab('manual')
    } catch (error) {
      console.error('Generation failed', error)
      alert(`Failed to generate text. ${error?.message || 'Check console.'}`)
    } finally {
      // If we queued the job, keep the UI busy until polling completes.
      if (!queued) setIsGenerating(false)
    }
  }

  useEffect(() => {
    if (!statusUrl) return

    let cancelled = false
    const interval = setInterval(async () => {
      try {
        const response = await fetch(statusUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })

        const contentType = response.headers.get('content-type') || ''
        if (!response.ok || !contentType.includes('application/json')) return

        const data = await response.json()
        if (cancelled) return

        if (data.status === 'completed') {
          if (typeof window.gtag === 'function' && queuedPrompt) {
            window.gtag('event', 'text_generated', {
              'theme': queuedPrompt.theme,
              'target_lang': queuedPrompt.target_lang,
              'length': queuedPrompt.length
            })
          }

          setData(prev => ({
            ...prev,
            title: data.title,
            content_raw: data.content_raw,
            language: data.language || queuedPrompt?.target_lang || aiPrompt.target_lang,
            image_url: data.image_url,
            tokens: data.tokens,
            spans: data.spans,
            questions: data.questions
          }))

          setActiveTab('manual')
          setIsGenerating(false)
          clearInterval(interval)
        } else if (data.status === 'failed') {
          setIsGenerating(false)
          clearInterval(interval)
          alert(`Failed to generate text. ${data.error || 'Unknown error.'}`)
        }
      } catch (e) {
        // keep polling
      }
    }, 1500)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [statusUrl, queuedPrompt])

  return (
    <UserLayout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">New Text</h1>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'manual' 
                ? 'border-emerald-600 text-emerald-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('manual')}
          >
            Manual Entry
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'ai' 
                ? 'border-emerald-600 text-emerald-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('ai')}
          >
            <span>✨ AI Generator</span>
          </button>
        </div>

        {/* AI Generator Tab */}
        {activeTab === 'ai' && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 mb-6 animate-in fade-in slide-in-from-top-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Theme / Topic</label>
              <input 
                type="text" 
                placeholder="e.g. Space Travel, Cooking, History of Rome..."
                value={aiPrompt.theme}
                onChange={e => setAiPrompt({...aiPrompt, theme: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Language</label>
                <select 
                  value={aiPrompt.target_lang}
                  onChange={e => setAiPrompt({...aiPrompt, target_lang: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Translate To</label>
                <select 
                  value={aiPrompt.native_lang}
                  onChange={e => setAiPrompt({...aiPrompt, native_lang: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  <option>English</option>
                  <option>French</option>
                  <option>Spanish</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Length</label>
              <div className="mt-1 flex gap-4">
                 {['short', 'medium', 'long'].map(l => (
                   <label key={l} className="flex items-center gap-2 cursor-pointer">
                     <input 
                       type="radio" 
                       name="length" 
                       value={l}
                       checked={aiPrompt.length === l}
                       onChange={e => setAiPrompt({...aiPrompt, length: e.target.value})}
                       className="text-emerald-600 focus:ring-emerald-500"
                     />
                     <span className="capitalize">{l}</span>
                   </label>
                 ))}
              </div>
            </div>

            <div className="pt-2">
              <button 
                onClick={handleGenerate}
                disabled={isGenerating || !aiPrompt.theme}
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>Processing...</> 
                ) : (
                  <>✨ Generate with AI</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Manual Form (Also serves as Review form after AI generation) */}
        <form onSubmit={submit} className={`bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 ${activeTab === 'ai' ? 'opacity-50 pointer-events-none filter blur-[1px]' : ''}`}>
          {data.tokens && (
             <div className="bg-emerald-50 text-emerald-800 p-3 rounded mb-4 text-sm">
               ✨ AI Content Loaded. Review and Create.
             </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input 
              type="text" 
              value={data.title} 
              onChange={e => setData('title', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
            {errors.title && <div className="text-red-500 text-sm">{errors.title}</div>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Language</label>
              <select 
                value={data.language} 
                onChange={e => setData('language', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              >
                 {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Difficulty (1-5)</label>
              <input 
                type="number" 
                min="1" max="5"
                value={data.difficulty} 
                onChange={e => setData('difficulty', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
          </div>

          <div>
            <TagSelector
              tags={tags}
              selectedTagIds={data.tag_ids || []}
              onChange={(tagIds) => setData('tag_ids', tagIds)}
              locale={userLocale}
            />
            {errors.tag_ids && <div className="text-red-500 text-sm mt-1">{errors.tag_ids}</div>}
            {errors.tags && <div className="text-red-500 text-sm mt-1">{errors.tags}</div>}
          </div>


          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
             <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
             
             {data.image_url ? (
               <div className="space-y-2">
                 <img src={data.image_url} alt="Cover" className="h-48 w-full object-cover rounded-md" />
                 <div className="flex gap-2">
                   <button 
                     type="button"
                     onClick={() => setData('image_url', '')}
                     className="text-sm text-red-600 hover:text-red-800 underline"
                   >
                     Remove Image
                   </button>
                 </div>
               </div>
             ) : (
               <div className="space-y-3">
                 <div>
                   <label className="block text-xs font-medium text-gray-500 mb-1">Upload File</label>
                   <input 
                     type="file" 
                     accept="image/*"
                     onChange={e => setData('image_file', e.target.files[0])}
                     className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                   />
                 </div>
                 <div className="text-center text-gray-400 text-xs">- OR -</div>
                 <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Paste URL</label>
                    <input 
                      type="text" 
                      placeholder="https://..."
                      value={data.image_url}
                      onChange={e => setData('image_url', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-sm"
                    />
                 </div>
               </div>
             )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Content</label>
            <textarea 
              rows="10"
              value={data.content_raw} 
              onChange={e => setData('content_raw', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono"
              placeholder="Paste text here..."
            />
            {errors.content_raw && <div className="text-red-500 text-sm">{errors.content_raw}</div>}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
            <h3 className="font-semibold text-gray-800">Generated Questions (Preview)</h3>
            {data.questions?.length > 0 ? (
              <div className="space-y-4 text-sm">
                {data.questions.map((q, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded border">
                    <p className="font-medium text-gray-900 mb-1">{i+1}. {q.content}</p>
                    <p className="text-emerald-700">✓ {q.correct_answer}</p>
                    <p className="text-gray-500 text-xs mt-1">Distractors: {q.distractors.join(', ')}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic text-sm">Questions will be generated automatically via AI.</p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <Link href="/admin/texts" className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</Link>
            <button 
              type="submit" 
              disabled={processing}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {processing ? 'Creating...' : 'Create & Tokenize'}
            </button>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}
